<template>
  <b-form-group :label="label" :state="state" class="clinic-search-select">
    <b-input-group>
      <b-form-input
        v-model="searchQuery"
        :placeholder="placeholder"
        :disabled="disabled || !!selectedItem"
        @input="debouncedSearch"
        @focus="showResults = true"
        @blur="handleBlur"
      />
      <b-input-group-append v-if="selectedItem">
        <b-button variant="outline-danger" @click="clearSelection">
          <i class="fas fa-times"></i>
        </b-button>
      </b-input-group-append>
      <b-input-group-append v-else>
        <b-button variant="outline-secondary" :disabled="loading">
          <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-search'"></i>
        </b-button>
      </b-input-group-append>
    </b-input-group>
    <div v-if="selectedItem" class="selected-item mt-2">
      <span class="badge badge-primary">
        {{ selectedItem.clinicCode }} - {{ selectedItem.clinicNameTh }}
      </span>
    </div>
    <div v-if="showResults && results.length && !selectedItem" class="search-results">
      <b-list-group>
        <b-list-group-item
          v-for="clinic in results"
          :key="clinic.clinicCode"
          button
          @mousedown.prevent="selectItem(clinic)"
        >
          <strong>{{ clinic.clinicCode }}</strong> - {{ clinic.clinicNameTh }}
          <small v-if="clinic.clinicNameEn" class="text-muted d-block">
            {{ clinic.clinicNameEn }}
          </small>
        </b-list-group-item>
      </b-list-group>
    </div>
    <div v-if="showResults && !loading && searchQuery.length >= 2 && results.length === 0" class="no-results mt-2">
      <small class="text-muted">No clinics found</small>
    </div>
  </b-form-group>
</template>

<script>
import debounce from '@/utils/debounce';
import SuperadminService from '@/services/SuperadminService';

export default {
  name: 'ClinicSearchSelect',
  props: {
    value: { type: Object, default: null },
    label: { type: String, default: 'Select Clinic' },
    placeholder: { type: String, default: 'Search clinic by name...' },
    disabled: { type: Boolean, default: false },
    state: { type: Boolean, default: null }
  },
  data() {
    return {
      searchQuery: '',
      results: [],
      loading: false,
      showResults: false,
      selectedItem: null
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.selectedItem = newVal;
        if (newVal) {
          this.searchQuery = `${newVal.clinicCode} - ${newVal.clinicNameTh}`;
        }
      }
    }
  },
  created() {
    this.debouncedSearch = debounce(this.performSearch, 300);
  },
  methods: {
    async performSearch() {
      if (this.searchQuery.length < 2) {
        this.results = [];
        return;
      }
      this.loading = true;
      try {
        const response = await SuperadminService.searchClinics({
          clinicName: this.searchQuery,
          limit: 10
        });
        this.results = response.data?.data?.clinics || [];
        this.showResults = true;
      } catch (error) {
        console.error('Clinic search failed:', error);
        this.results = [];
      } finally {
        this.loading = false;
      }
    },
    selectItem(clinic) {
      this.selectedItem = clinic;
      this.searchQuery = `${clinic.clinicCode} - ${clinic.clinicNameTh}`;
      this.showResults = false;
      this.$emit('input', clinic);
    },
    clearSelection() {
      this.selectedItem = null;
      this.searchQuery = '';
      this.results = [];
      this.$emit('input', null);
    },
    handleBlur() {
      setTimeout(() => {
        this.showResults = false;
      }, 200);
    }
  }
};
</script>

<style scoped>
.clinic-search-select {
  position: relative;
}
.search-results {
  position: absolute;
  z-index: 1000;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.search-results .list-group-item {
  cursor: pointer;
}
.search-results .list-group-item:hover {
  background-color: #f8f9fa;
}
</style>
